import type { TechId } from './ids';
/**
 * Economic influence and levers (GDD §6). Influence builds up every cycle
 * from trade and exports and is spent on four levers, each unlocked by a
 * technology (#27). The exchange also raises its own events: a glut or a
 * shortage of a good is announced a cycle ahead. Starting values for tuning.
 */
export const LEVER_IDS = ['embargo', 'tariff', 'dumping', 'sanctions'] as const;
export type LeverId = (typeof LEVER_IDS)[number];

export interface LeverDef {
  readonly id: LeverId;
  /** i18n key: `lever.<id>.name`. */
  readonly i18nKey: `lever.${LeverId}`;
  /** Influence it costs (each backer pays it for sanctions). */
  readonly cost: number;
  /** How long it lasts, in economic cycles. */
  readonly cycles: number;
  /** Technology that unlocks it (#27). */
  readonly requiresTech: TechId;
}

export const LEVERS: Readonly<Record<LeverId, LeverDef>> = {
  // The target cannot buy your faction's cheap good on the exchange, and your
  // contracts selling to it stop loading.
  embargo: {
    id: 'embargo',
    i18nKey: 'lever.embargo',
    cost: 30,
    cycles: 10,
    requiresTech: 'financialEngineering',
  },
  // Foreign loaded caravans pay a share of their cargo's value on entering your zones.
  tariff: {
    id: 'tariff',
    i18nKey: 'lever.tariff',
    cost: 20,
    cycles: 10,
    requiresTech: 'financialEngineering',
  },
  // Sell a batch and crash the price for two cycles (GDD §6).
  dumping: {
    id: 'dumping',
    i18nKey: 'lever.dumping',
    cost: 25,
    cycles: 2,
    requiresTech: 'interstellarFinance',
  },
  // A coalition of 2+ (or the reserve issuer alone): the target pays +15% on
  // every exchange deal and its contracts freeze (GDD §6).
  sanctions: {
    id: 'sanctions',
    i18nKey: 'lever.sanctions',
    cost: 40,
    cycles: 10,
    requiresTech: 'interstellarFinance',
  },
};

export const INFLUENCE = {
  /** Influence a player can hold at most. */
  max: 100,
  /** Per cycle: this × the player's share of exchange turnover (0..1)... */
  perTradeShare: 10,
  /** ...plus this per 100 ГС of goods it delivered by contract this cycle... */
  perExportHundred: 1,
  /** ...plus this while its currency is the reserve. */
  reserveIssuer: 5,
} as const;

export const LEVER_EFFECTS = {
  /** Tariff: share of a caravan cargo's value paid on entering a foreign zone. */
  tariffShare: 0.1,
  /** Dumping: the price drops to this share and holds there for the lever's cycles. */
  dumpingPriceFactor: 0.7,
  /** Sanctions: extra cost on every exchange deal of the target. */
  sanctionsSurcharge: 0.15,
  /** Backers sanctions need, unless the reserve issuer imposes them alone. */
  sanctionsBackers: 2,
} as const;

/**
 * Exchange events (GDD §6: «перепроизводство, дефицит» — announced ahead).
 * The exchange has no stock, so pressure stands in for it: net units sold by
 * all players over the window × the good's price impact, i.e. how far in log
 * terms the trade pushed the price. The strongest pressure past the threshold
 * announces a glut (sold) or a shortage (bought); if it still holds a cycle
 * later, the price drops (or jumps). One event at a time, then a quiet spell.
 * Tuned on bot matches: 4–6 events a 75-minute match with 3–4 players.
 */
export const MARKET_EVENTS = {
  windowCycles: 5,
  thresholdPressure: 4,
  cooldownCycles: 10,
  glutPriceFactor: 0.75,
  shortagePriceFactor: 1.3,
} as const;
